import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useLexicalNodeSelection} from '@lexical/react/useLexicalNodeSelection';
import {mergeRegister} from '@lexical/utils';
import type {BaseSelection, LexicalCommand, LexicalEditor, NodeKey} from 'lexical';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {useCallback, useEffect, useRef, useState} from 'react';

import styles from './ImageNode.module.css';
import {$isVideoNode} from './VideoNode';

export const RIGHT_CLICK_VIDEO_COMMAND: LexicalCommand<MouseEvent> = createCommand(
  'RIGHT_CLICK_VIDEO_COMMAND'
);

/**
 * Without a poster, Safari renders an empty box until playback; a media fragment makes it (and
 * Chrome) paint the frame at that offset once metadata loads.
 */
const withPreviewFragment = (src: string, poster: string | undefined) =>
  poster || src.includes('#') ? src : `${src}#t=0.1`;

const VideoElement = ({
  altText,
  className,
  videoRef,
  src,
  poster,
  width = '100%',
  height,
}: {
  altText?: string;
  className?: string;
  videoRef: {current: null | HTMLVideoElement};
  src: string;
  poster?: string;
  height?: '100%' | number;
  width?: '100%' | number;
}) => (
  <video
    className={className || undefined}
    src={withPreviewFragment(src, poster)}
    poster={poster}
    aria-label={altText || undefined}
    ref={videoRef}
    style={{
      display: 'block',
      maxWidth: '100%',
      height: height ?? 'auto',
      width,
    }}
    preload="metadata"
    playsInline
    controls
    draggable="false"
  />
);

export const VideoComponent = ({
  src,
  poster,
  altText,
  nodeKey,
  width,
  height,
}: {
  altText?: string;
  height?: '100%' | number;
  nodeKey: NodeKey;
  poster?: string;
  src: string;
  width?: '100%' | number;
}) => {
  const videoRef = useRef<null | HTMLVideoElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<BaseSelection | null>(null);
  const activeEditorRef = useRef<LexicalEditor | null>(null);
  const isEditable = editor.isEditable();

  const onDelete = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isVideoNode(node)) {
          node.remove();
          return true;
        }
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  const onEnter = useCallback(
    (event: KeyboardEvent) => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (
        isSelected &&
        $isNodeSelection(latestSelection) &&
        latestSelection.getNodes().length === 1
      ) {
        if (buttonElem !== null && buttonElem !== document.activeElement) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [isSelected]
  );

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (buttonRef.current === event.target) {
        $setSelection(null);
        editor.update(() => {
          setSelected(true);
          const parentRootElement = editor.getRootElement();
          if (parentRootElement !== null) {
            parentRootElement.focus();
          }
        });
        return true;
      }
      return false;
    },
    [editor, setSelected]
  );

  const onClick = useCallback(
    (payload: MouseEvent) => {
      if (!isEditable) {
        return false;
      }

      const event = payload;

      if (event.target === videoRef.current) {
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }

      return false;
    },
    [isEditable, isSelected, setSelected, clearSelection]
  );

  const onRightClick = useCallback(
    (event: MouseEvent): void => {
      editor.getEditorState().read(() => {
        const latestSelection = $getSelection();
        const domElement = event.target as HTMLElement;
        if (
          domElement.tagName === 'VIDEO' &&
          $isRangeSelection(latestSelection) &&
          latestSelection.getNodes().length === 1
        ) {
          editor.dispatchCommand(RIGHT_CLICK_VIDEO_COMMAND, event as MouseEvent);
        }
      });
    },
    [editor]
  );

  useEffect(() => {
    let isMounted = true;
    const rootElement = editor.getRootElement();
    const unregister = mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        if (isMounted) {
          setSelection(editorState.read(() => $getSelection()));
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<MouseEvent>(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand<MouseEvent>(RIGHT_CLICK_VIDEO_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === videoRef.current) {
            // TODO This is just a temporary workaround for FF to behave like other browsers.
            // Ideally, this handles drag & drop too (and all browsers).
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_LOW)
    );

    rootElement?.addEventListener('contextmenu', onRightClick);

    return () => {
      isMounted = false;
      unregister();
      rootElement?.removeEventListener('contextmenu', onRightClick);
    };
  }, [
    clearSelection,
    editor,
    isSelected,
    nodeKey,
    onDelete,
    onEnter,
    onEscape,
    onClick,
    onRightClick,
    setSelected,
  ]);

  const draggable = isEditable && isSelected && $isNodeSelection(selection);
  const isFocused = isEditable && isSelected;

  return (
    <>
      <div
        draggable={draggable}
        style={{verticalAlign: 'middle', paddingLeft: 1, paddingRight: 1}}
        className={
          isFocused
            ? `${styles.focused} ${$isNodeSelection(selection) ? styles.draggable : ''}`
            : undefined
        }
      >
        <VideoElement
          src={src}
          poster={poster}
          altText={altText}
          videoRef={videoRef}
          width={width}
          height={height}
        />
      </div>
    </>
  );
};
